import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

type ToastTone = "success" | "danger" | "info";

interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  detail?: string;
}

interface ToastValue {
  push: (tone: ToastTone, title: string, detail?: string) => void;
  success: (title: string, detail?: string) => void;
  error: (title: string, detail?: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, title: string, detail?: string) => {
      const id = nextId++;
      setToasts((current) => [...current, { id, tone, title, detail }]);
      window.setTimeout(() => dismiss(id), tone === "danger" ? 7000 : 4000);
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({
      push,
      success: (title: string, detail?: string) =>
        push("success", title, detail),
      error: (title: string, detail?: string) => push("danger", title, detail),
    }),
    [push],
  );

  const toneStyles: Record<ToastTone, { border: string; accent: string }> = {
    success: { border: "var(--success)", accent: "var(--success-bg)" },
    danger: { border: "var(--danger)", accent: "var(--danger-bg)" },
    info: { border: "var(--info)", accent: "var(--info-bg)" },
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-5 right-5 z-[100] flex w-[min(92vw,26rem)] flex-col gap-2"
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="panel pointer-events-auto flex items-start gap-3 border-l-4 p-3 pr-2"
            style={{
              borderLeftColor: toneStyles[toast.tone].border,
              background: "var(--surface-raised)",
            }}
          >
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold">{toast.title}</p>
              {toast.detail ? (
                <p className="text-dim mt-0.5 text-[12px] break-words">
                  {toast.detail}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="text-dim hover:text-body rounded px-1.5 text-lg leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside ToastProvider");
  return context;
}
